import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";

const TutorAuthLogin = () => {
    const [step, setStep] = useState(1); // 1: Email input, 2: Code verification, 3: New password (if needed)
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [timer, setTimer] = useState(0);
    const [login, setLogin] = useState({ 
        email: '',
        verification_code: '',
        password: '',
        confirm_password: ''
    });
    const { email, verification_code, password, confirm_password } = login;

    const handleInput = (e) => {
        setLogin({ ...login, [e.target.name]: e.target.value });
    };

    // Start countdown timer for resend code
    const startTimer = () => {
        setTimer(120);
        const interval = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Step 1: Send verification code to email
    const handleSendCode = async (e) => {
        e.preventDefault();
        
        if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
            toast.error("Please enter a valid email address");
            return;
        }

        setIsSubmitting(true);
        
        try {
            const response = await axios.post('https://pastq.eddcodesgh.com/qApi/client/auth/_send_verification.php', 
                { email },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );
            
            if (response.data.status === 'success') {
                toast.success("Verification code sent to your email!");
                setStep(2);
                startTimer();
            } else {
                toast.error(response.data.message || "Failed to send verification code");
            }
        } catch (error) {
            console.error('Send code error:', error);
            toast.error(error.response?.data?.message || "An error occurred. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Step 2: Verify code and check if password reset is needed
    const handleVerifyCode = async (e) => {
        e.preventDefault();
        
        if (!verification_code || verification_code.length !== 6) {
            toast.error("Please enter the 6-digit verification code");
            return;
        }

        setIsSubmitting(true);
        
        try {
            const response = await axios.post('https://pastq.eddcodesgh.com/qApi/client/auth/_verify_code.php', 
                { email, verification_code },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );
            
            if (response.data.status === 'success') {
                if (response.data.needs_password_reset) {
                    toast.success("Code verified! Please set your password");
                    setStep(3);
                } else {
                    // Tutor has password, proceed to login
                    toast.success("Code verified! Logging you in...");
                    handleFinalLogin();
                }
            } else {
                toast.error(response.data.message || "Invalid verification code");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "An error occurred. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Step 3: Set new password
    const handleSetPassword = async (e) => {
        e.preventDefault();
        
        if (!password || password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        
        if (password !== confirm_password) {
            toast.error("Passwords do not match");
            return;
        }

        setIsSubmitting(true);
        
        try {
            const response = await axios.post('https://pastq.eddcodesgh.com/qApi/client/tutors/_set_password.php', 
                { email, verification_code, password },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );
            
            if (response.data.status === 'success') {
                toast.success("Password set successfully! Logging you in...");
                handleFinalLogin();
            } else {
                toast.error(response.data.message || "Failed to set password");
            }
        } catch (error) {
            console.error('Set password error:', error);
            toast.error(error.response?.data?.message || "An error occurred. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Final login after verification
    const handleFinalLogin = async () => {
        try {
            const response = await axios.post('https://pastq.eddcodesgh.com/qApi/client/auth/_login.php', 
                { email, verification_code },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );
            
            if (response.data.status === 'success') {
                toast.success("Login successful!");
                localStorage.setItem("tutorAppId", response.data.tutor_data.id);
                localStorage.setItem("email", email);
                localStorage.setItem("token", response.data.token);
                localStorage.setItem("phone", response.data.tutor_data.phone);
                localStorage.setItem("tutorData", JSON.stringify(response.data.tutor_data || {}));
                window.location.href = '/tutor/dashboard';
            } else {
                toast.error(response.data.message || "Login failed");
            }
        } catch (error) {
            console.error('Final login error:', error);
            toast.error(error.response?.data?.message || "An error occurred. Please try again.");
        }
    };

    // Resend verification code
    const handleResendCode = async () => {
        if (timer > 0) return;
        
        setIsSubmitting(true);
        
        try {
            const response = await axios.post('https://pastq.eddcodesgh.com/qApi/client/auth/_resend_code.php', 
                { email },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );
            
            if (response.data.status === 'success') {
                toast.success("New verification code sent!");
                startTimer();
            } else {
                toast.error(response.data.message || "Failed to resend code");
            }
        } catch (error) {
            console.error('Resend code error:', error);
            toast.error(error.response?.data?.message || "An error occurred. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="">
            <section className="section register min-vh-100 d-flex flex-column align-items-center justify-content-center py-4">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-4 col-md-6 d-flex flex-column align-items-center justify-content-center">
                            
                            {/* Header */}
                            <div className="text-center mb-4">
                                <h2 className="h2 fw-bold mb-3">Tutor Login</h2>
                                <p className="text-muted">
                                    {step === 1 && "Enter your registered email to receive verification code"}
                                    {step === 2 && "Enter the 6-digit code sent to your email"}
                                    {step === 3 && "Set your password for first-time login"}
                                </p>
                            </div>
                            
                            {/* Login Card */}
                            <div className="card mb-3 shadow-lg rounded" style={{border: 'none', width: '100%'}}>
                                <div className="card-body p-4">
                                    
                                    {/* Step 1: Email Input */}
                                    {step === 1 && (
                                        <form onSubmit={handleSendCode} noValidate>
                                            <div className="mb-3">
                                                <label htmlFor="email" className="form-label fw-semibold">
                                                    Email Address <span className="text-danger">*</span>
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0">
                                                        <i className="bi bi-envelope text-secondary"></i>
                                                    </span>
                                                    <input 
                                                        type="email" 
                                                        name="email" 
                                                        className="form-control border-start-0" 
                                                        id="email" 
                                                        value={email}
                                                        onChange={handleInput}
                                                        required 
                                                        placeholder="tutor@example.com"
                                                    />
                                                </div>
                                                <small className="text-muted">
                                                    Must be a registered tutor email
                                                </small>
                                            </div>

                                            <button
                                                className="btn btn w-100 text-white py-2 fw-semibold mt-3" 
                                                style={{ background: "#096b00" }} 
                                                type="submit"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner-border text-white spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Sending Code...
                                                    </>
                                                ) : (
                                                    'Send Verification Code'
                                                )}
                                            </button>
                                        </form>
                                    )}

                                    {/* Step 2: Code Verification */}
                                    {step === 2 && (
                                        <form onSubmit={handleVerifyCode} noValidate>
                                            <div className="mb-3">
                                                <label htmlFor="verification_code" className="form-label fw-semibold">
                                                    6-Digit Verification Code <span className="text-danger">*</span>
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0">
                                                        <i className="bi bi-shield-lock text-secondary"></i>
                                                    </span>
                                                    <input 
                                                        type="text" 
                                                        name="verification_code" 
                                                        className="form-control border-start-0" 
                                                        id="verification_code" 
                                                        value={verification_code}
                                                        onChange={handleInput}
                                                        required 
                                                        placeholder="123456"
                                                        maxLength="6"
                                                        pattern="[0-9]{6}"
                                                    />
                                                </div>
                                                <small className="text-muted">
                                                    Enter the 6-digit code sent to {email}
                                                </small>
                                            </div>

                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 text-decoration-none"
                                                    onClick={() => setStep(1)}
                                                >
                                                    <i className="bi bi-arrow-left me-1"></i>
                                                    Change Email
                                                </button>
                                                
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 text-decoration-none"
                                                    onClick={handleResendCode}
                                                    disabled={timer > 0 || isSubmitting}
                                                >
                                                    {timer > 0 ? (
                                                        `Resend in ${timer}s`
                                                    ) : (
                                                        "Resend Code"
                                                    )}
                                                </button>
                                            </div>

                                            <button
                                                className="btn btn w-100 text-white py-2 fw-semibold" 
                                                style={{ background: "#096b00" }} 
                                                type="submit"
                                                disabled={isSubmitting || !verification_code || verification_code.length !== 6}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner-border text-white spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Verifying...
                                                    </>
                                                ) : (
                                                    'Verify Code'
                                                )}
                                            </button>
                                        </form>
                                    )}

                                    {/* Step 3: Set Password */}
                                    {step === 3 && (
                                        <form onSubmit={handleSetPassword} noValidate>
                                            <div className="mb-3">
                                                <label htmlFor="password" className="form-label fw-semibold">
                                                    New Password <span className="text-danger">*</span>
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0">
                                                        <i className="bi bi-lock text-secondary"></i>
                                                    </span>
                                                    <input 
                                                        type="password" 
                                                        name="password" 
                                                        className="form-control border-start-0" 
                                                        id="password" 
                                                        value={password}
                                                        onChange={handleInput}
                                                        required 
                                                        placeholder="At least 6 characters"
                                                        minLength="6"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <label htmlFor="confirm_password" className="form-label fw-semibold">
                                                    Confirm Password <span className="text-danger">*</span>
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0">
                                                        <i className="bi bi-lock-fill text-secondary"></i>
                                                    </span>
                                                    <input 
                                                        type="password" 
                                                        name="confirm_password" 
                                                        className="form-control border-start-0" 
                                                        id="confirm_password" 
                                                        value={confirm_password}
                                                        onChange={handleInput}
                                                        required 
                                                        placeholder="Confirm your password"
                                                        minLength="6"
                                                    />
                                                </div>
                                            </div>

                                            <button
                                                className="btn btn w-100 text-white py-2 fw-semibold" 
                                                style={{ background: "#096b00" }} 
                                                type="submit"
                                                disabled={isSubmitting || !password || !confirm_password || password.length < 6}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner-border text-white spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Setting Password...
                                                    </>
                                                ) : (
                                                    'Set Password & Login'
                                                )}
                                            </button>
                                        </form>
                                    )}
                                    
                                    <div className="text-center mt-4 pt-3 border-top">
                                        <Link to="/" className="text-decoration-none">
                                            <i className="bi bi-house me-1"></i>
                                            Back to Home
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Info */}
                            <div className="text-center mt-3">
                                <small className="text-muted">
                                    New tutor? <Link to="/tutors/apply" className="text-decoration-none fw-semibold">Apply here</Link>
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default TutorAuthLogin;